import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useSeason } from '../../hooks/useSeason';
import type { Motif } from '../../lib/seasons';

const GLYPHS: Record<Motif, readonly string[]> = {
  snow: ['❄', '❄', '•'],
  hearts: ['♥', '♥', '♡'],
  shamrocks: ['☘', '♣'],
  petals: ['✿', '❀', '•'],
  bubbles: ['○', '◦'],
  stars: ['★', '✦'],
  fireflies: ['•'],
  leaves: ['🍂', '🍁'],
  bats: ['🦇'],
};

const TIMING: Record<Motif, { base: number; spread: number }> = {
  snow: { base: 18, spread: 8 },
  hearts: { base: 12, spread: 6 },
  shamrocks: { base: 13, spread: 6 },
  petals: { base: 12, spread: 6 },
  bubbles: { base: 14, spread: 7 },
  stars: { base: 3, spread: 3 },
  fireflies: { base: 16, spread: 8 },
  leaves: { base: 11, spread: 6 },
  bats: { base: 12, spread: 6 },
};

function buildBits(count: number, motif: Motif) {
  const glyphs = GLYPHS[motif];
  const timing = TIMING[motif];
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const drift = (n % 2 === 0 ? 1 : -1) * (18 + (n % 6) * 14);
    const duration = timing.base + (n % timing.spread);
    // Negative delay so the loop is already in motion when the page opens.
    const style = {
      left: `${(n * 47) % 100}%`,
      animationDelay: `${-((n * 1.37) % duration).toFixed(2)}s`,
      animationDuration: `${duration}s`,
      fontSize: motif === 'bats' ? `${26 + (n % 4) * 8}px` : `${18 + (n % 5) * 6}px`,
      '--drift': `${drift}px`,
      ...(motif === 'stars' ? { '--top': `${6 + ((n * 29) % 82)}%` } : {}),
    } as CSSProperties;
    return { id: i, glyph: glyphs[i % glyphs.length], style };
  });
}

/**
 * Sparse seasonal shapes over the dashboard. Clicks pass through.
 * Hidden when the viewer asks for reduced motion.
 */
export function SeasonOverlay() {
  const season = useSeason();
  const [motionOk, setMotionOk] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [slim, setSlim] = useState(() => window.matchMedia('(max-width: 640px)').matches);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrow = window.matchMedia('(max-width: 640px)');
    const onMotion = () => setMotionOk(!motion.matches);
    const onNarrow = () => setSlim(narrow.matches);
    motion.addEventListener('change', onMotion);
    narrow.addEventListener('change', onNarrow);
    return () => {
      motion.removeEventListener('change', onMotion);
      narrow.removeEventListener('change', onNarrow);
    };
  }, []);

  const count = slim ? Math.max(6, Math.ceil(season.count / 2)) : season.count;
  const bits = useMemo(
    () => buildBits(count, season.motif),
    [count, season.motif]
  );

  if (!motionOk) return null;

  return (
    <div className="season-sky" data-motif={season.motif} aria-hidden="true">
      {bits.map((bit) => (
        <span key={bit.id} className="season-bit" style={bit.style}>
          {bit.glyph}
        </span>
      ))}
    </div>
  );
}
